import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import { toast } from "components/ui/toast/Toast";
import { useDomainVerificationToken } from "lib/hooks/api/auth/sso/useDomainVerificationToken";
import { useVerifyDomain } from "lib/hooks/api/auth/sso/useVerifyDomain";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { pluralise } from "lib/utils";
import { generateDnsRecordHost, generateDnsRecordName } from "./ProviderUtils";

type DomainVerificationSectionProps = {
  providerId: string;
  domains: string[];
  domainVerified: boolean;
  domainVerificationToken?: string | null;
  onVerified: () => void;
};

export default function DomainVerificationSection({
  providerId,
  domains,
  domainVerified,
  domainVerificationToken = null,
  onVerified,
}: DomainVerificationSectionProps) {
  const { data: tokenData, isFetching: isFetchingToken } = useDomainVerificationToken(providerId, {
    enabled: !domainVerificationToken,
  });

  const verificationToken = domainVerificationToken ?? tokenData?.domainVerificationToken;

  const { mutate: verifyToken, isLoading } = useVerifyDomain({
    onSuccess: () => {
      toast.success("Domain verified successfully");
      onVerified();
    },
    onError: () => {
      toast.error("Could not verify domain. Please wait a few minutes then try again.");
    },
  });

  return (
    <Flex column gap={12}>
      <Text h3>Domain verification</Text>

      <Text description>
        {domainVerified
          ? `${pluralise(domains.length, "domain")} verified. You can now use SSO for your organisation.`
          : `Verify ownership of ${pluralise(
              domains.length,
              "domain",
            )} by adding a DNS TXT record.`}
      </Text>

      <Text description>Add the following DNS TXT record to verify your domain:</Text>
      <Flex column gap={16}>
        {domains.map((domain) => (
          <DnsRecord key={domain}>
            <RecordRow alignItems="center">
              <RecordLabel>Domain</RecordLabel>
              <Text as="code">{domain}</Text>
            </RecordRow>
            <RecordRow alignItems="center">
              <RecordLabel>Name (excluding your domain)</RecordLabel>
              <Text as="code">{generateDnsRecordHost(providerId)}</Text>
            </RecordRow>
            <RecordRow alignItems="center">
              <RecordLabel>Type</RecordLabel>
              <Text as="code">TXT</Text>
            </RecordRow>
            <RecordRow alignItems="center">
              <RecordLabel>Value</RecordLabel>
              <Text as="code">
                {isFetchingToken ? (
                  <UIcon icon="spinner" />
                ) : (
                  verificationToken ?? "Error loading token"
                )}
              </Text>
            </RecordRow>
            <RecordRow alignItems="center">
              <RecordLabel>Full record name</RecordLabel>
              <Text as="code">{generateDnsRecordName(providerId, domain)}</Text>
            </RecordRow>
          </DnsRecord>
        ))}
      </Flex>
      <Text description>
        Most DNS providers (including Cloudflare, Route 53 and Google Cloud DNS) append your domain
        to whatever you type in the name field, so use the name without the domain. Only use the
        full record name if your provider asks for one.
      </Text>
      <Text description>
        After adding the record, click below to verify. DNS changes can take up to 48 hours to
        propagate but usually take effect within a few minutes.
      </Text>
      <Text description>You can remove the DNS TXT record after verification is complete.</Text>
      <Button onClick={() => verifyToken({ providerId })} loading={isLoading} width="fit-content">
        Verify domain
      </Button>
    </Flex>
  );
}

const DnsRecord = styled.div(({ theme }) => ({
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  borderRadius: 8,
  overflow: "hidden",
}));

const RecordRow = styled(Flex)(({ theme }) => ({
  padding: "8px 16px",
  "&:not(:last-child)": {
    borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
  },
}));

const RecordLabel = styled(Text)(({ theme }) => ({
  width: 190,
  flexShrink: 0,
  fontWeight: theme.font.fontWeightMedium,
  fontSize: 13,
  color: theme.colors.sysTextSecondary,
}));
